/**
 * LoginPage.tsx — /app/login sign-in form
 *
 * Purpose:    Public sign-in page for the ɳSentry SaaS app zone.
 * Inputs:     email + password form fields; auth.signIn from @/lib/api
 * Outputs:    Session cookie on success → redirect to /app/monitors
 * Constraints: Dark-first styling per app convention; dashboard-zone pages
 *              hardcode English (no i18n, matching existing dashboard pages).
 * SOT:        nsentry SaaS app zone
 */
import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { auth } from '@/lib/api'
import { useAuth } from '@/lib/auth-context'

export function LoginPage() {
  const navigate = useNavigate()
  const { refetch } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const result = await auth.signIn(email, password)
    if (result.error) {
      setError(result.error.message)
      setLoading(false)
      return
    }

    await refetch()
    navigate('/app/monitors')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <Link to="/" className="text-2xl font-bold text-sky-700 dark:text-sky-400">
            ɳSentry
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-brand-ink">Welcome back</h1>
          <p className="mt-1 text-sm text-brand-muted">Sign in to your ɳSentry account</p>
        </div>

        <div className="rounded-2xl border border-brand-subtle bg-brand-surface p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3" role="alert">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-brand-secondary">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-brand-secondary">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                placeholder="8+ characters"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-sky-700 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-brand-muted">
            No account?{' '}
            <Link to="/app/signup" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
              Sign up free
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
